import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { RequirePermission } from '@/components/auth';
import { trimDecimalZeros } from '@/utils/decimal';
import type { ClientPricingDetail, PricingUpdateRequest, ServicePricing } from './types';
import { pricingUpdateSchema, paymentServiceLabel } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PricingEditorProps {
  isOpen: boolean;
  pricing: ClientPricingDetail | undefined;
  /** Rail being edited — the row the operator clicked Edit on. */
  serviceCode: string | null;
  loading: boolean;
  isUpdating: boolean;
  onSave: (clientId: string, payload: PricingUpdateRequest) => Promise<void>;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

const DEFAULT_FEE = '0';

type FeeKey =
  | 'outgoing_fee_fixed_value'
  | 'outgoing_fee_percent_value'
  | 'incoming_fee_fixed_value'
  | 'incoming_fee_percent_value';

/**
 * L'autre facon de tarifer le meme sens.
 *
 * Un forfait et un pourcentage sortants s'excluent, comme leurs equivalents
 * entrants. Mais un forfait sortant et un pourcentage entrant sont deux
 * tarifs distincts et coexistent tres bien.
 */
function counterpartOf(key: FeeKey): FeeKey | null {
  const pairs: Record<string, FeeKey> = {
    outgoing_fee_fixed_value: 'outgoing_fee_percent_value',
    outgoing_fee_percent_value: 'outgoing_fee_fixed_value',
    incoming_fee_fixed_value: 'incoming_fee_percent_value',
    incoming_fee_percent_value: 'incoming_fee_fixed_value',
  };
  return pairs[key] ?? null;
}

type FeeDraft = Record<FeeKey, string>;

interface FeeFieldDef {
  key: FeeKey;
  label: string;
  unit: 'amount' | 'percent';
}

/**
 * The four terms of a rail, grouped by direction. One term applies per
 * direction (#83): a rail charges a fixed fee *or* a percentage, never both.
 * Both fields stay present so the operator can switch between them, and a
 * zero is a real term.
 */
const DIRECTIONS: { title: string; hint: string; fields: FeeFieldDef[] }[] = [
  {
    title: 'Outgoing payments',
    hint: 'Charged when the client sends funds on this service.',
    fields: [
      { key: 'outgoing_fee_fixed_value', label: 'Fixed fee', unit: 'amount' },
      { key: 'outgoing_fee_percent_value', label: 'Percentage fee', unit: 'percent' },
    ],
  },
  {
    title: 'Incoming payments',
    hint: 'Charged when the client receives funds on this service.',
    fields: [
      { key: 'incoming_fee_fixed_value', label: 'Fixed fee', unit: 'amount' },
      { key: 'incoming_fee_percent_value', label: 'Percentage fee', unit: 'percent' },
    ],
  },
];

const ALL_FIELDS: FeeFieldDef[] = DIRECTIONS.flatMap((d) => d.fields);

type FieldErrors = Partial<Record<FeeKey, string>>;

function toDraft(service: ServicePricing | undefined): FeeDraft {
  return {
    outgoing_fee_fixed_value: trimDecimalZeros(service?.outgoing_fee_fixed_value ?? DEFAULT_FEE),
    outgoing_fee_percent_value: trimDecimalZeros(
      service?.outgoing_fee_percent_value ?? DEFAULT_FEE,
    ),
    incoming_fee_fixed_value: trimDecimalZeros(service?.incoming_fee_fixed_value ?? DEFAULT_FEE),
    incoming_fee_percent_value: trimDecimalZeros(
      service?.incoming_fee_percent_value ?? DEFAULT_FEE,
    ),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PricingEditor({
  isOpen,
  pricing,
  serviceCode,
  loading,
  isUpdating,
  onSave,
  onClose,
}: PricingEditorProps) {
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  const service = useMemo(
    () => pricing?.services.find((s) => s.payment_service_code === serviceCode),
    [pricing, serviceCode],
  );
  const initial = useMemo(() => toDraft(service), [service]);

  const [draft, setDraft] = useState<FeeDraft>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    setDraft(initial);
    setErrors({});
  }, [initial]);

  useEffect(() => {
    if (isOpen && !loading) {
      const timer = setTimeout(() => {
        saveButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, loading]);

  const isDirty = ALL_FIELDS.some(({ key }) => draft[key] !== initial[key]);

  // Clear a single field's error on edit so a stale message doesn't linger
  // while the operator is correcting the value.
  const clearFieldError = useCallback((field: FeeKey) => {
    setErrors((prev) => {
      if (prev[field] === undefined) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const buildPayload = useCallback(
    () => ({ payment_service_code: serviceCode ?? '', ...draft }),
    [serviceCode, draft],
  );

  const validateField = useCallback(
    (field: FeeKey) => {
      const result = pricingUpdateSchema.safeParse(buildPayload());
      if (result.success) {
        clearFieldError(field);
        return;
      }
      const issue = result.error.issues.find((i) => i.path.includes(field));
      if (!issue) {
        // Retirer la cle, et non y ranger `undefined` : `hasErrors` compte les
        // cles, pas les valeurs. Une cle a `undefined` laissait le bouton
        // Enregistrer desactive sans qu'aucun message ne s'affiche.
        clearFieldError(field);
        return;
      }
      setErrors((prev) => ({ ...prev, [field]: issue.message }));
    },
    [buildPayload, clearFieldError],
  );

  const validateAll = useCallback((): PricingUpdateRequest | null => {
    const result = pricingUpdateSchema.safeParse(buildPayload());
    if (result.success) {
      setErrors({});
      return result.data;
    }
    const fieldErrors: FieldErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path[0] as FeeKey | undefined;
      if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
    }
    setErrors(fieldErrors);
    return null;
  }, [buildPayload]);

  const handleSave = async () => {
    if (!pricing || !serviceCode) return;
    const validated = validateAll();
    if (!validated) return;
    await onSave(String(pricing.client_id), validated);
  };

  const hasErrors = Object.keys(errors).length > 0;
  const companyName = pricing?.company_name ?? 'Client';
  const railLabel = serviceCode ? paymentServiceLabel(serviceCode) : 'Service';

  return (
    <Modal
      isOpen={isOpen}
      title={`Edit Pricing — ${companyName} · ${railLabel}`}
      onClose={onClose}
      className="max-w-3xl"
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          {/* Save mutates pricing — defense-in-depth gate on MANAGE_PRICING.
              The /pricing route is already gated, but explicit per-button
              wrapping survives any future route-gate refactor. */}
          <RequirePermission any={['MANAGE_PRICING']}>
            <Button
              ref={saveButtonRef}
              onClick={() => void handleSave()}
              disabled={!isDirty || hasErrors || isUpdating || loading}
            >
              {isUpdating ? 'Saving...' : 'Save Pricing'}
            </Button>
          </RequirePermission>
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner size="md" text="Loading pricing..." />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <p data-testid="pricing-exclusive-hint" className="mb-4 text-[12px] text-[#666]">
            One term per direction: a rail charges either a fixed fee or a percentage of the amount,
            never both. Entering one sets the other back to 0.
          </p>

          <div data-testid="pricing-cards-grid" className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {DIRECTIONS.map((direction) => (
              <section
                key={direction.title}
                data-testid={`pricing-card-${direction.fields[0].key.split('_')[0]}`}
                className="rounded-lg border border-[#e0e0e0] bg-white p-4"
              >
                <h3 className="text-[14px] font-semibold text-[#333]">{direction.title}</h3>
                <p className="mb-3 mt-0.5 text-[12px] text-[#999]">{direction.hint}</p>

                {direction.fields.map(({ key, label, unit }) => (
                  <div key={key} className="mt-4 first:mt-0">
                    <label
                      htmlFor={key}
                      className="mb-1 block text-[12px] font-semibold text-[#666]"
                    >
                      {label} {unit === 'percent' ? '(%)' : '($)'}
                    </label>
                    <div className="relative">
                      {unit === 'amount' && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#666]">
                          $
                        </span>
                      )}
                      <input
                        id={key}
                        type="text"
                        inputMode="decimal"
                        value={draft[key]}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Un seul type de frais par sens : saisir l'un
                          // remet l'autre a zero (#83). Le serveur refuse les
                          // deux ensemble ; le faire ici evite a l'operateur
                          // de decouvrir le refus a l'enregistrement.
                          const opposite = counterpartOf(key);
                          // Le voisin n'est remis a zero que si la valeur
                          // saisie est reellement un tarif. Une saisie vide,
                          // nulle ou invalide le laisse tel quel — et son
                          // erreur eventuelle doit alors rester visible, sans
                          // quoi on masquerait un pourcentage a 150 en
                          // touchant au forfait.
                          const resetsOpposite = opposite !== null && Number(value) > 0;
                          setDraft((prev) => ({
                            ...prev,
                            [key]: value,
                            ...(resetsOpposite ? { [opposite]: '0' } : {}),
                          }));
                          clearFieldError(key);
                          if (resetsOpposite) clearFieldError(opposite);
                        }}
                        onBlur={() => validateField(key)}
                        aria-invalid={errors[key] ? true : undefined}
                        aria-describedby={errors[key] ? `${key}-error` : undefined}
                        className={inputClasses(
                          !!errors[key],
                          unit === 'amount' ? 'pl-8' : undefined,
                        )}
                      />
                      {unit === 'percent' && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#666]">
                          %
                        </span>
                      )}
                    </div>
                    {errors[key] && (
                      <p id={`${key}-error`} className="mt-1.5 text-xs text-[#c62828]" role="alert">
                        {errors[key]}
                      </p>
                    )}
                  </div>
                ))}
              </section>
            ))}
          </div>

          {/* The spread is global to the client, so it is shown for context
              and edited from the client's detail screen — never here, where
              it would look like a property of this one rail. */}
          <section
            data-testid="pricing-fx-note"
            className="mt-5 rounded-lg border border-[#e0e0e0] bg-[#f8f9fa] p-4"
          >
            <h3 className="text-[14px] font-semibold text-[#333]">FX Spread</h3>
            <div className="mt-1 text-[18px] font-bold text-[#6B0F1A]">
              {trimDecimalZeros(pricing?.fx_spread ?? DEFAULT_FEE)}%
            </div>
            <p className="mt-1 text-[12px] text-[#999]">
              Applies to every conversion for this client, whichever service the funds then use.
              Edit it from the client&apos;s detail page.
            </p>
          </section>
        </form>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function inputClasses(hasError: boolean, extraClasses?: string): string {
  return [
    'w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors',
    'placeholder:text-[#999]',
    'focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]',
    hasError ? 'border-[#c62828] focus:border-[#c62828] focus:ring-[#c62828]' : 'border-[#e0e0e0]',
    extraClasses ?? '',
  ].join(' ');
}
